import { FinishingRoute, FinishingRouteStatus, ProcessKey } from '@app/models/production/finishing-route';
import _ from 'lodash';

export const ROLLING_PROCESS_ID = '29';

export class FinishingRouteUtil {
  /**
   * Check if a finishing route has the Rolling process (processId "29") anywhere in its pending processes
   * Use this to show a general warning that the route will eventually need a transfer
   */
  public static hasRollingProcessPending(route: FinishingRoute): boolean {
    const pendingProcesses = this.getPendingProcesses(route);
    return pendingProcesses.some((p) => p.processId === ROLLING_PROCESS_ID);
  }

  /**
   * Check if only the Rolling process (processId "29") remains as the last pending process
   * Use this to show a warning that the route needs to be transferred NOW before continuing
   */
  public static isOnlyRollingProcessRemaining(route: FinishingRoute): boolean {
    const pendingProcesses = this.getPendingProcesses(route);
    return pendingProcesses.length === 1 && pendingProcesses[0].processId === ROLLING_PROCESS_ID;
  }

  /**
   * Get all pending (not started and not completed) processes for a route
   */
  public static getPendingProcesses(route: FinishingRoute): ProcessKey[] {
    return route.route?.filter((p) => _.isNil(p.startTime) && _.isNil(p.endTime)) || [];
  }

  /**
   * Get all completed processes for a route
   */
  public static getCompletedProcesses(route: FinishingRoute): ProcessKey[] {
    return route.route?.filter((p) => !_.isNil(p.endTime)) || [];
  }

  /**
   * Get all in-progress processes for a route (started but not completed)
   */
  public static getInProgressProcesses(route: FinishingRoute): ProcessKey[] {
    return route.route?.filter((p) => !_.isNil(p.startTime) && _.isNil(p.endTime)) || [];
  }

  /**
   * Check if a route is fully completed
   */
  public static isRouteCompleted(route: FinishingRoute): boolean {
    return route.route?.every((p) => !_.isNil(p.endTime)) ?? false;
  }

  /**
   * Check if a route is awaiting QA (all finishing processes completed except Rolling)
   */
  public static isAwaitingQA(route: FinishingRoute): boolean {
    return route.status === FinishingRouteStatus.AwaitingQA;
  }

  public static isQAApproved(route: FinishingRoute): boolean {
    return route.status === FinishingRouteStatus.QAApproved;
  }

  /**
   * Check if all finishing processes (except Rolling) are completed
   * This is useful for calculating the status dynamically if needed
   */
  public static areAllFinishingProcessesCompleted(route: FinishingRoute): boolean {
    const finishingProcesses = route.route?.filter((p) => p.processId !== ROLLING_PROCESS_ID) || [];
    return finishingProcesses.length > 0 && finishingProcesses.every((p) => !_.isNil(p.endTime));
  }

  /**
   * Check if the route has a Rolling process (processId "29") in any state (pending, in-progress, or completed)
   * Use this to determine if a Finishing to Rolling transfer should be loaded/displayed
   */
  public static hasRollingProcess(route: FinishingRoute): boolean {
    return route.route?.some((p) => p.processId === ROLLING_PROCESS_ID) ?? false;
  }

  /**
   * Check if the Rolling process (processId "29") is already in progress or completed
   * Use this to allow weighing a roll at a Rolling weight station
   */
  public static isRollingProcessStartedOrCompleted(route: FinishingRoute): boolean {
    return route.route?.some((p) => p.processId === ROLLING_PROCESS_ID && !_.isNil(p.startTime)) ?? false;
  }
}
